




















































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import VueDraggableResizable from 'vue-draggable-resizable';
import { fieldTextSizes } from '@/fields/context-menu/constants/field-text-sizes';
import { IUser } from '@/types/User';
import { EAlignFieldType } from '@/fields/context-menu/types/enums/align-field-type';
import { ContextMenuHelper } from '@/fields/context-menu/helpers/context-menu.helper';
import FieldContextMenu from '@/fields/context-menu/components/FieldContextMenu.vue';
import Icon from '@/components/Icon.vue';
import { IFieldPosition } from '@/fields/types/field-position';
import { IField } from '@/fields/types/field';
import {
  calculateFieldPlacementOnMultidrag,
  calculateFieldContainerPosition,
} from '@/fields/helpers/field-placement.helper';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';
import { EnumDocumentType } from '@/enums/DocumentType';
import { FieldHelper } from '@/fields/helpers/field.helper';

@Component({
  components: {
    FieldContextMenu,
    VueDraggableResizable,
    Icon,
  },
  methods: { multipleFieldActions: ContextMenuHelper.multipleFieldActions },
  data: () => ({
    EnumDocumentType,
  }),
})
export default class DragMultipleFieldsContainer extends Vue {
  @Prop() fields!: IField[];
  @Prop() pages: IDocumentPage[];
  @Prop() sender: IUser;
  @Prop() recipients: IRecipient[];
  @Prop() pageWidth: number;
  @Prop() documentType: EnumDocumentType;

  menuOpen: boolean = false;
  dragging: boolean = false;
  textSizes: number[] = fieldTextSizes;
  textSize: number = this.displayedTextSize;
  position: IFieldPosition = calculateFieldContainerPosition(this.fields);

  @Watch('fields')
  watchingFields(newValue: IField[]) {
    this.position = calculateFieldContainerPosition(newValue);
  }

  get flipped(): boolean {
    const marginRight: number = this.pageWidth - this.position.left - this.position.width;
    const isSmallWindow = window.innerWidth <= 1264;
    return marginRight < 210 && isSmallWindow;
  }

  get top() {
    return this.position.top;
  }

  get left() {
    return this.position.left;
  }

  get width() {
    return this.position.width;
  }

  get height() {
    return this.position.height;
  }

  get borderStyle(): string {
    return `top: ${this.top}px; left: ${this.left}px; 
    width: ${this.width}px; height: ${this.height}px`;
  }
  get marginTop(): number {
    return this.top + this.borderSize;
  }

  get marginLeft(): number {
    return this.left + this.borderSize;
  }

  get borderSize(): number {
    return 1;
  }

  get displayedTextSize(): number {
    if (!this.isSameFieldHeight) {
      return 0;
    }
    return FieldHelper.getTextSize(this.fields[0].position, this.pageWidth, this.textSizes);
  }

  get isSameFieldHeight(): boolean {
    return this.fields.every(field => field.position.height === this.fields[0].position.height);
  }

  get isTemplate(): boolean {
    return this.documentType === EnumDocumentType.Template;
  }

  get isFieldsEditable(): boolean {
    return this.isTemplate || this.fields.every((field: IField) => field.isEditable);
  }

  handleClickOutside() {
    if (!this.dragging) {
      this.$emit('deselectAll');
    }
  }

  handleDragging() {
    this.dragging = true;
    this.$emit('dragging');
  }

  setMenuVisibility(open: boolean) {
    this.menuOpen = open;
    if (open) {
      this.textSize = this.displayedTextSize;
    }
  }

  handleDragStop(left: number, top: number) {
    const repositionedFields: IField[] = calculateFieldPlacementOnMultidrag(
      this.fields,
      this.position,
      {
        ...this.position,
        left,
        top,
      }
    );
    const newPositions = repositionedFields.map((field: IField) => field.position);
    const oldPositions: IFieldPosition[] = this.fields.map((field: IField) => field.position);
    if (JSON.stringify(newPositions) !== JSON.stringify(oldPositions)) {
      this.savePosition(repositionedFields);
    }
    setTimeout(() => {
      this.dragging = false;
      this.$emit('dragging');
    });
  }

  handleUpdate(payload: Partial<IField>[]) {
    this.$emit('save', payload);
  }

  handleDelete() {
    this.$emit('delete');
    this.setMenuVisibility(false);
  }

  handleMove(payload: string) {
    this.$emit('move', payload);
    this.setMenuVisibility(false);
  }

  handleDeselectAll() {
    this.setMenuVisibility(false);
    this.$emit('deselectAll');
  }

  handleDuplicate() {
    this.$emit('duplicate');
    this.setMenuVisibility(false);
  }

  handleAlign(actionType: EAlignFieldType) {
    this.$emit('align', actionType);
  }

  handleTextSize(size: number) {
    this.textSize = size;
    this.$emit('textSize', FieldHelper.getSaveFieldHeight(size));
  }

  savePosition(fields: IField[]) {
    this.$emit('savePosition', fields);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') {
      return;
    }
    if (this.menuOpen) {
      this.setMenuVisibility(false);
    } else {
      this.handleDeselectAll();
    }
  }

  mounted() {
    this.position = calculateFieldContainerPosition(this.fields);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }
}
